import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable11 = ({ data }) => {
  const pdfRef = useRef();

  // Extract Values
  const labels = data.map((item) => item.agegrp_label);
  const employmentValues = data.map((item) => Number(item.employment_2024));
  const femalePercentValues = data.map((item) =>
    Number(item.female_percentage)
  );

  // Insights
  const maxIndex = femalePercentValues.indexOf(
    Math.max(...femalePercentValues)
  );
  const minIndex = femalePercentValues.indexOf(
    Math.min(...femalePercentValues)
  );
  const avgFemalePercent = (
    femalePercentValues.reduce((a, b) => a + b, 0) / femalePercentValues.length
  ).toFixed(2);

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Employment in 2024",
        data: employmentValues,
        backgroundColor: "#2C92B850",
        borderColor: "#2C92B8",
        borderWidth: 2,
        yAxisID: "y1",
      },
      {
        type: "line",
        label: "Female Percentage",
        data: femalePercentValues,
        borderColor: "#EC4899",
        backgroundColor: "#EC489930",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 6,
        pointBackgroundColor: "#EC4899",
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 14 } },
      },
      title: {
        display: true,
        text: "Employment & Female Participation in 2024 (By Age Group)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y1: {
        title: { display: true, text: "Total Employment" },
        position: "left",
      },
      y2: {
        title: { display: true, text: "Female Percentage (%)" },
        position: "right",
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: "Age Group" },
      },
    },
  };

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 60, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 95, 20);
      pdf.line(10, 32, 200, 32);

      pdf.addImage(img, "PNG", 5, 35, width, height);

      pdf.line(10, 260, 200, 260);

      pdf.addImage(utahData, "PNG", 20, 265, 25, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 50, 276);
      pdf.addImage(bodex, "PNG", 63, 268, 44, 10);
      pdf.text("using", 110, 276);
      pdf.addImage(kpi, "PNG", 123, 265, 22, 18);

      pdf.text(`Version: 20250916.v3.198`, 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 140, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("female_participation_2024_agegroup.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          📊 Employment & Female Participation in 2024 by Age Group
        </h2>

        {/* Summary Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-green-100 shadow text-center">
            <p className="text-gray-600 text-sm">Highest Female Ratio</p>
            <p className="text-xl font-bold text-green-700">
              {labels[maxIndex]} ({femalePercentValues[maxIndex]}%)
            </p>
          </div>

          <div className="p-4 rounded-xl bg-red-100 shadow text-center">
            <p className="text-gray-600 text-sm">Lowest Female Ratio</p>
            <p className="text-xl font-bold text-red-700">
              {labels[minIndex]} ({femalePercentValues[minIndex]}%)
            </p>
          </div>

          <div className="p-4 rounded-xl bg-blue-100 shadow text-center">
            <p className="text-gray-600 text-sm">Average Female %</p>
            <p className="text-xl font-bold text-blue-700">
              {avgFemalePercent}%
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Employment (2024)</th>
                  <th className="p-2">Female %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.agegrp_label}
                    className="hover:bg-gray-100 transition"
                  >
                    <td className="p-2 font-medium">{row.agegrp_label}</td>
                    <td className="p-2 text-blue-700 font-semibold">
                      {Number(row.employment_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2 text-red-600 font-bold">
                      {row.female_percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Export button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable11;
